import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Peer } from 'peerjs';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { encrypt, randomString } from './helpers/utilities';
import {
  SIGNAL_HOST,
  SIGNAL_PATH,
  SIGNAL_PORT
} from './configs';
import {
  alert,
  updateRealtime,
  updateRealtimeSignal,
  updateSignal,
  getPatientSignal,
  updateRealtimeSignalGuest
} from './actions';

let peer = null;

const SignalConnect = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [process, setProcess] = useState('initial');
  const auth = useSelector(state => state.auth);
  const referral = useSelector(state => state.referral);
  const { profile } = auth;
  const realtime = useSelector(state => state.realtime);
  const [signal, setSignal] = useState();
  const [cookieSignal, setCookieSignal] = useState(null);
  const [codeUser, setCodeUser] = useState(null);
  const [cookie, setCookie] = useCookies();
  const [doctorName, setDoctorName] = useState('');
  const [relationPatients] = useState([]);

  useEffect(() => {
    if (auth.loggedIn && !peer && !cookieSignal) {
      setCookieSignal(randomString(15));
      if (_.has(auth, 'profile.docCode')) {
        setCodeUser(auth.profile.docCode);
        setDoctorName(`${auth.profile.firstName} ${auth.profile.lastName}`);
      }
    }
  }, [auth, cookie, setCookieSignal, cookieSignal, setCookie]);

  const updateDataSignal = useCallback(async () => {
    const signalResult = await updateSignal(codeUser, cookieSignal);
    if (!_.has(signalResult, 'message')) {
      alert({
        type: 'warning',
        title: 'Update signal',
        message: 'Update signal error',
        fill: false
      });
    }
  }, [codeUser, cookieSignal]);

  useEffect(() => {
    if (codeUser && cookieSignal) {
      updateDataSignal(codeUser, cookieSignal);
    }
  }, [codeUser, cookieSignal]);

  const broadcastToPatients = useCallback(() => {
    async function sendDataSignal(peer2, doctorName2) {
      peer2.listAllPeers(res => {
        res.forEach(arg => {
          if (arg.search('PT') >= 0 && _.indexOf(relationPatients, arg.split('-')[0]) >= 0) {
            const conn = peer2.connect(arg).on('open', () => {
              conn.send({
                signalId: peer2._id,
                to: arg.split('-')[0],
                actionCode: 'B002',
                actionMessage: `แพทย์ ${doctorName2} เข้าใช้งานระบบตอนนี้`
              });
            });
          }
        });
      });
    }
    if (peer && doctorName) {
      sendDataSignal(peer, doctorName);
    }
  }, [doctorName]);

  const initSignal = useCallback(signalID => {
    localStorage.setItem('signalID', signalID);
    peer = new Peer(signalID, {
      host: SIGNAL_HOST,
      port: SIGNAL_PORT,
      path: `/${SIGNAL_PATH}`,
      secure: true
    });
    peer.on('disconnected', () => {
      peer.reconnect();
    });
    peer.on('open', () => {
      dispatch(updateRealtimeSignal('connected'));
      broadcastToPatients();
    });
    peer.on('connection', conn => {
      conn.on('data', data => {
        if (data.actionCode === 'A001') {
          dispatch(updateRealtime('calling-request', true, { refCode: data.data.meetRoom, patCode: data.signalId }));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('calling-request'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'A002') {
          dispatch(updateRealtime('calling-accept', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('calling-accept'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'A003') {
          dispatch(updateRealtime('reject-calling', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'danger',
              title: t('Calling'),
              message: t('The patient reject call from you'),
              icon: 'ion:notifications'
            });
          }
        }
        if (data.actionCode === 'A004') {
          dispatch(updateRealtime('calling-timeout', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('calling-timeout'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'A005') {
          dispatch(updateRealtime('call-hangup', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('call-hangup'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'A006') {
          dispatch(updateRealtime('calling-cancel', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('calling-cancel'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'A008') {
          dispatch(updateRealtime('calling-busy', true));
          if (_.has(data, 'showpopup') && data.showpopup) {
            alert({
              type: 'success',
              title: t('Calling'),
              message: t('calling-busy'),
              icon: 'ion:notifications',
              fill: false
            });
          }
        }
        if (data.actionCode === 'CH001') {
          let newMessage = '-';
          if (data.data.message.type === 'message') {
            newMessage = data.data.message.message;
          } else if (data.data.message.type === 'file' && data.data.message.fileType === 'image') {
            newMessage = t('File image');
          } else if (data.data.message.type === 'file' && data.data.message.fileType === 'file') {
            newMessage = t('File pdf');
          }
          const { pathname, search } = window.location;
          const dataSet = {
            refCode: data?.data?.meetRoom,
            goback: `${pathname}${search}`
          };
          const token = encodeURIComponent(encrypt(dataSet).replace(/\//g, '---'));
          let link = '#';
          if (!pathname.startsWith('/telemed')) {
            link = `/chats/${token}`;
          }
          alert({
            type: 'success',
            title: t('Received a new message'),
            message: `#${data.data.meetRoom}: ${newMessage}`,
            icon: 'ic:outline-chat',
            fill: false,
            link
          });
          dispatch(updateRealtime('chat-receive', true, { refCode: data.data.meetRoom, docCode: data.to, message: data.data.message }));
        }
        if (data.actionCode === 'B001') {
          alert({
            type: 'success',
            title: t('User is online'),
            message: `${data.actionMessage}`,
            icon: 'mdi:account-online',
            fill: false
          });
        }
      });
    });
    if (!signal && peer) {
      setSignal(peer);
    }
    peer.on('error', error => {
      if (error) {
        dispatch(updateRealtimeSignal('disconnected'));
      }
    });
  }, [dispatch, broadcastToPatients]);

  useEffect(() => {
    const reconnectInterval = setInterval(() => {
      if (peer && peer?.disconnected) {
        peer.reconnect();
      }
    }, 10000);
    return () => clearInterval(reconnectInterval);
  }, []);

  const getDataSignalEasycall = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                const dataSignal = {
                  signalId: signal._id,
                  to: data.patCode,
                  actionCode: 'TP001',
                  actionMessage: 'Starting consulting',
                  data: {
                    meetRoom: data.refCode,
                    caseInfo: {},
                    userRequestInfo: {}
                  },
                  showpopup: false
                };
                conn.on('open', () => {
                  conn.send(dataSignal);
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const getDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              dispatch(updateRealtime('connect-signal', false, data));
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                const dataSignal = {
                  signalId: signal._id,
                  to: data.patCode,
                  actionCode: 'A001',
                  actionMessage: 'Calling Request',
                  data: {
                    meetRoom: data.refCode,
                    caseInfo: {},
                    userRequestInfo: {}
                  },
                  showpopup: false
                };
                conn.on('open', () => {
                  conn.send(dataSignal);
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const connectSignal = data => {
    getDataSignal(data);
  };

  const connectSignalEasycall = data => {
    getDataSignalEasycall(data);
  };

  const getAcceptCallingDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A002',
                    actionMessage: 'Calling Accept',
                    data: {
                      refCode: data.refCode
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const acceptCalling = data => {
    getAcceptCallingDataSignal(data);
  };

  const getRejectCallingDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A003',
                    actionMessage: 'Calling Reject',
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const rejectCalling = data => {
    getRejectCallingDataSignal(data);
  };

  const getCallingTimeoutDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A004',
                    actionMessage: 'Calling Timeout',
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const callingTimeout = data => {
    getCallingTimeoutDataSignal(data);
  };

  const getCallingHanupDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A005',
                    actionMessage: 'Calling Hangup',
                    data: {
                      meetRoom: data.refCode
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const getCallingHanupDataSignalEasycall = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'TP002',
                    actionMessage: 'Stoping consulting',
                    data: {
                      meetRoom: data.refCode
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const callingHanup = data => {
    getCallingHanupDataSignal(data);
  };

  const callingHanupEasycall = data => {
    getCallingHanupDataSignalEasycall(data);
  };

  const getCallingCancelDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A006',
                    actionMessage: 'Calling Cancel',
                    data: {
                      meetRoom: data.refCode
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const callingCancel = data => {
    getCallingCancelDataSignal(data);
  };

  const getCallingBusyDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      dispatch({ type: 'IS_LOADING', status: true, names: { WAIT_AJAX: true } });
      const signalPatientResult = await getPatientSignal(data.patCode);
      dispatch({ type: 'IS_LOADING', status: false, names: { WAIT_AJAX: false } });
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: data.patCode,
                    actionCode: 'A008',
                    actionMessage: 'Calling Busy',
                    data: {
                      meetRoom: data.refCode
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const callingBusy = data => {
    getCallingBusyDataSignal(data);
  };

  const getChatSentDataSignal = useCallback(async data => {
    if (_.has(data, 'patCode')) {
      if (typeof data.patCode === 'string' && data.patCode.split('-').length > 1) {
        const [one] = data.patCode.split('-');
        data.patCode = one;
      }
      const signalPatientResult = await getPatientSignal(data.patCode);
      if (signalPatientResult !== '') {
        if (signal) {
          signal.listAllPeers(res => {
            if (res.includes(`${data.patCode}-${signalPatientResult}`)) {
              const conn = signal.connect(`${data.patCode}-${signalPatientResult}`);
              if (conn) {
                conn.on('open', () => {
                  conn.send({
                    signalId: signal._id,
                    to: `${data.patCode}-${signalPatientResult}`,
                    actionCode: 'CH001',
                    actionMessage: 'Chat message',
                    data: {
                      meetRoom: data.refCode,
                      message: data.message
                    },
                    showpopup: false
                  });
                });
              }
            }
          });
        }
      }
    }
  }, [signal]);

  const chatSent = data => {
    getChatSentDataSignal(data);
  };

  const guestSent = action => {
    if (_.has(referral, 'detail.guest') && action !== '') {
      if (action === 'connected') {
        referral.detail.guest.forEach(g => {
          const conn = signal.connect(`${g}`);
          if (conn) {
            conn.on('open', () => {
              conn.send({
                signalId: signal._id,
                to: g,
                actionCode: 'G001',
                actionMessage: 'Connected',
                data: {
                  meetRoom: referral.detail.refCode,
                  message: 'Connected'
                },
                showpopup: false
              });
            });
          }
        });
      } else {
        referral.detail.guest.forEach(g => {
          const conn = signal.connect(`${g}`);
          if (conn) {
            conn.on('open', () => {
              conn.send({
                signalId: signal._id,
                to: g,
                actionCode: 'G002',
                actionMessage: 'Hangup',
                data: {
                  meetRoom: referral.detail.refCode,
                  message: 'Hangup'
                },
                showpopup: false
              });
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    if (auth && auth.loggedIn && profile && profile.docCode && process === 'initial' && cookieSignal) {
      setProcess('');
      initSignal(`${profile.docCode}-${cookieSignal}`);
    }
  }, [profile, process, cookieSignal]);

  useEffect(() => {
    if (realtime.name === 'connect-signal' && realtime.status) {
      dispatch(updateRealtime('connect-signal', false));
      connectSignal(realtime.data);
    }
    if (realtime.name === 'connect-signal-easycall' && realtime.status) {
      dispatch(updateRealtime('connect-signal-easycall', false));
      connectSignalEasycall(realtime.data);
    }
    if (realtime.name === 'calling-accept' && realtime.status) {
      dispatch(updateRealtime('calling-accept', false, { accept: true }));
      acceptCalling(realtime.data);
    }
    if (realtime.name === 'reject-calling' && realtime.status) {
      dispatch(updateRealtime('reject-calling-clear', false));
      rejectCalling(realtime.data);
    }
    if (realtime.name === 'calling-timeout' && realtime.status) {
      dispatch(updateRealtime('calling-timeout', false));
      callingTimeout(realtime.data);
    }
    if (realtime.name === 'calling-hangup' && realtime.status) {
      dispatch(updateRealtime('calling-hangup', false));
      callingHanup(realtime.data);
    }
    if (realtime.name === 'calling-cancel' && realtime.status) {
      dispatch(updateRealtime('calling-cancel', false));
      callingCancel(realtime.data);
    }
    if (realtime.name === 'calling-busy' && realtime.status) {
      dispatch(updateRealtime('calling-busy', false));
      callingBusy(realtime.data);
    }
    if (realtime.name === 'chat-sent' && realtime.status) {
      dispatch(updateRealtime('chat-sent', false));
      chatSent(realtime.data);
    }
    if (realtime.name === 'reconnect-signal' && realtime.status) {
      dispatch(updateRealtime('reconnect-signal', false));
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
    if (realtime.guestAction !== '') {
      guestSent(realtime.guestAction);
      dispatch(updateRealtimeSignalGuest(''));
    }
    if (realtime.name === 'calling-hangup-easycall' && realtime.status) {
      dispatch(updateRealtime('calling-hangup-easycall', false));
      callingHanupEasycall(realtime.data);
    }
  }, [dispatch, realtime]);
};

export default SignalConnect;
